import type { PopupProductDto } from "@/types/api.types";
import { formatCurrency } from "@/utils/currency";

interface ProductCardProps {
  currencyCode: string;
  isSelected: boolean;
  locale: string;
  product: PopupProductDto;
  onToggle: (productId: string) => void;
}

export function ProductCard({
  currencyCode,
  isSelected,
  locale,
  product,
  onToggle,
}: ProductCardProps) {
  return (
    <button
      className="grid w-full cursor-pointer grid-cols-[24px_80px_minmax(0,1fr)] items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-neutral-50"
      onClick={() => onToggle(product.id)}
      type="button"
    >
      <span
        aria-hidden="true"
        className={`flex h-4 w-4 items-center justify-center rounded-[4px] border transition ${
          isSelected
            ? "border-neutral-900 bg-neutral-900 text-white"
            : "border-neutral-900 bg-white text-transparent"
        }`}
      >
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 12 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 6.2 4.8 9 10 3"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      </span>

      <div className="flex h-20 w-20 items-center justify-center overflow-hidden">
        {product.product_image ? (
          <img
            alt={product.product_name}
            className="h-full w-full object-cover"
            src={product.product_image}
          />
        ) : (
          <span className="text-xs font-semibold tracking-[0.18em] text-neutral-400 uppercase">
            Item
          </span>
        )}
      </div>

      <div className="min-w-0">
        <div className="text-sm md:text-base leading-6 font-bold text-neutral-800">
          {product.amount}x {product.product_name}
        </div>
        {product.tr_description ? (
          <div
            className="mt-1 max-w-[18rem] text-xs leading-4 text-neutral-500 [&_p]:m-0"
            dangerouslySetInnerHTML={{ __html: product.tr_description }}
          />
        ) : null}
        <div className="mt-2 flex items-center gap-2 text-sm leading-5">
          <span className="font-bold text-neutral-800">
            {product.price.formatted ??
              formatCurrency(product.price.value, currencyCode, locale)}
          </span>
          {product.old_price ? (
            <span className="relative text-neutral-400 before:absolute before:top-1/2 before:left-0 before:right-0 before:-rotate-[10deg] before:border-t before:border-red-500 before:content-['']">
              {product.old_price.formatted ??
                formatCurrency(product.old_price.value, currencyCode, locale)}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}
